import { assertNever } from "@/shared/lib/assertNever";
import { Stack, type StackProps, Typography } from "@mui/material";
import { useMemo } from "react";
import { BaseIcon } from "../BaseIcons";

type BaseMessageCardStatus = "success" | "error" | "warning";

export type BaseMessageCardProps = {
  status: BaseMessageCardStatus;
  message: string;
  size?: "medium" | "small";
} & StackProps;

export const BaseMessageCard = ({
  status,
  message,
  ...props
}: BaseMessageCardProps) => {
  const baseColor = useMemo(() => {
    switch (status) {
      case "success":
        return "#52A48B";
      case "error":
        return "#EA583B";
      case "warning":
        return "#DC6803";
      default:
        assertNever(status);
        throw new Error(`unkown status ${status}`);
    }
  }, [status]);

  const baseBgColor = useMemo(() => {
    switch (status) {
      case "success":
        return "#F0FAF7";
      case "error":
        return "#FEF4F2";
      case "warning":
        return "#FEF0C7";
      default:
        assertNever(status);
        throw new Error(`unkown status ${status}`);
    }
  }, [status]);

  const messageIcon = useMemo(() => {
    switch (status) {
      case "success":
        return "check";
      case "error":
      case "warning":
        return "alertCircle";
    }
  }, [status]);

  return (
    <Stack
      spacing={1}
      direction="row"
      px={props.size === "small" ? 1 : 2}
      py={props.size === "small" ? 0.75 : 1.5}
      border={`1px solid ${baseColor}`}
      borderRadius={props.size === "small" ? 2 : 4}
      bgcolor={baseBgColor}
      justifyItems="center"
      {...props}
    >
      <BaseIcon
        name={messageIcon}
        htmlColor={baseColor}
        sx={{ fontSize: "20px" }}
      />
      <Typography
        variant={props.size === "small" ? "caption" : "body2"}
        color={baseColor}
      >
        {message}
      </Typography>
    </Stack>
  );
};
